import { useEffect, useRef, useState, type ReactNode } from "react";
import { cn } from "@/lib/utils";
import { AddContentFlowOverlays } from "@/components/navigation/AddContentFlowOverlays";
import { useAddContentFlow } from "@/hooks/useAddContentFlow";
import { useMenu } from "./MenuContext";
import { NavigationMenu } from "./NavigationMenu";

/**
 * One menu and one workspace. Phones reveal the menu by sliding the page;
 * larger windows reserve space beside it. The shared page frame is unchanged.
 */
export function MenuLayout({ children }: { children: ReactNode }) {
  const menu = useMenu();
  const open = menu?.open ?? false;
  const docked = menu?.docked ?? false;
  const close = menu?.closeMenu;
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem("stasht:sidebar-collapsed") === "true";
    } catch {
      return false;
    }
  });
  const toggleCollapsed = () => {
    const next = !collapsed;
    setCollapsed(next);
    try {
      localStorage.setItem("stasht:sidebar-collapsed", String(next));
    } catch {
      /* Navigation remains usable without device storage. */
    }
  };
  // One add flow for the app; its sheets render outside the drawer so they
  // outlive it (the native capture sheet's counterpart).
  const flow = useAddContentFlow();
  const headingRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    if (!open || !close) return;
    headingRef.current?.focus({ preventScroll: true });
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, close]);

  return (
    <div
      className="navigation-layout relative min-h-0 flex-1 overflow-hidden bg-background"
      data-sidebar-collapsed={docked && collapsed}
    >
      <div className="navigation-drawer" inert={!open && !docked} aria-hidden={!open && !docked}>
        <NavigationMenu
          flow={flow}
          close={close ?? (() => {})}
          headingRef={headingRef}
          collapsed={docked && collapsed}
          onToggleCollapsed={toggleCollapsed}
        />
      </div>
      <div
        className={cn(
          "navigation-surface relative z-10 h-full overflow-hidden bg-background transition-[transform,border-radius] duration-300 ease-out",
          open && "rounded-[32px] shadow-2xl",
        )}
        data-menu-open={open}
      >
        <div className="h-full" inert={open}>
          {children}
        </div>
        {open && (
          <button
            type="button"
            aria-label="Close menu"
            onClick={close}
            className="absolute inset-0 z-[100] cursor-default"
          />
        )}
      </div>
      <AddContentFlowOverlays flow={flow} />
    </div>
  );
}
